import { useCallback, useEffect, useRef, useState } from 'react';
import { uploadFile, type MediaConfig } from '@/api/media';

/**
 * Staged-attachment model for the agent composer.
 *
 * TWIN: `Frontend/chat-widget/src/hooks/useAttachmentStaging.ts`. Same model, same failure
 * handling, different API client — the two apps are separate packages with no shared workspace.
 * Keep them in step; an agent and a customer should experience attaching a file identically.
 */
export interface StagedAttachment {
  localId: string;
  fileName: string;
  sizeBytes: number;
  mimeType: string;
  /** Object URL for an image thumbnail. Owned by this hook, revoked by this hook. */
  previewUrl?: string;
  status: 'uploading' | 'ready' | 'failed';
  error?: string;
  /** Server-side attachment id. Only present once status === 'ready'. */
  uploadedId?: string;
}

/**
 * Client-side cap per message. Mirrors the hub's own MaxAttachmentsPerMessage — the server is
 * still the authority and rejects a sixth; this just stops the UI queueing uploads that are
 * going to be refused.
 */
export const MAX_STAGED_ATTACHMENTS = 5;

const GENERIC_PASTE_NAMES = new Set(['image.png', 'image.jpeg', 'image.jpg', 'image.webp', '']);

const MIME_EXTENSION: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
};

/**
 * Monotonic local id. Deliberately NOT crypto.randomUUID(): jest setups in this repo polyfill
 * that to a CONSTANT when the runtime lacks it, which would give every staged file the same
 * localId — colliding React keys, and a remove that empties the whole tray. A counter is correct
 * here regardless; these ids never leave the browser.
 */
let stagedSeq = 0;
const nextLocalId = () => `staged-${++stagedSeq}`;

/**
 * Chrome names every pasted screenshot `image.png`, so three of them are indistinguishable in
 * the transcript and to the customer receiving them. Only generic names are rewritten — a real
 * file the agent copied from disk keeps the name they know it by.
 */
export function nameIfPasted(file: File, now: Date = new Date()): File {
  if (!GENERIC_PASTE_NAMES.has((file.name || '').toLowerCase())) return file;

  const ext = MIME_EXTENSION[file.type] ?? (file.name.split('.').pop() || 'png');
  const stamp = now.toISOString().replace(/[:.]/g, '-').replace('T', '_').slice(0, 19);
  try {
    return new File([file], `pasted-${stamp}.${ext}`, { type: file.type });
  } catch {
    return file;
  }
}

/** Server policy check, mirroring what MediaAttachmentService enforces on upload. */
export function validateAgainstPolicy(file: File, cfg: MediaConfig): string | null {
  if (cfg.maxFileSizeBytes > 0 && file.size > cfg.maxFileSizeBytes) {
    const mb = Math.round((cfg.maxFileSizeBytes / (1024 * 1024)) * 10) / 10;
    return `File too large. Maximum size is ${mb} MB.`;
  }
  // An empty allow-list means the server advertised no restriction — let the server be the
  // authority rather than blocking every upload client-side.
  if (cfg.allowedMimeTypes.length > 0 && !cfg.allowedMimeTypes.includes(file.type)) {
    return 'File type not allowed.';
  }
  return null;
}

export function useAttachmentStaging(sessionId: string | undefined, mediaConfig: MediaConfig) {
  const [staged, setStaged] = useState<StagedAttachment[]>([]);

  // Every object URL this hook has minted. A ref, not derived from `staged`, so the unmount
  // cleanup sees the true set even if the last state update has not flushed — an un-revoked
  // object URL pins the whole image in memory.
  const previewUrls = useRef<Set<string>>(new Set());

  const revoke = useCallback((url?: string) => {
    if (!url) return;
    URL.revokeObjectURL(url);
    previewUrls.current.delete(url);
  }, []);

  useEffect(
    () => () => {
      previewUrls.current.forEach((u) => URL.revokeObjectURL(u));
      previewUrls.current.clear();
    },
    [],
  );

  const addFiles = useCallback(
    async (incoming: File[]) => {
      // The feature flag is enforced HERE, not only on the paperclip's render. Paste has no
      // button to hide, so without this it would be a way to upload while the feature is dark.
      if (!mediaConfig.enabled || !sessionId || incoming.length === 0) return;

      const room = MAX_STAGED_ATTACHMENTS - staged.length;
      if (room <= 0) return;
      const files = incoming.slice(0, room).map((f) => nameIfPasted(f));

      const entries: StagedAttachment[] = files.map((file) => {
        const problem = validateAgainstPolicy(file, mediaConfig);
        let previewUrl: string | undefined;
        if (!problem && file.type.startsWith('image/')) {
          previewUrl = URL.createObjectURL(file);
          previewUrls.current.add(previewUrl);
        }
        return {
          localId: nextLocalId(),
          fileName: file.name,
          sizeBytes: file.size,
          mimeType: file.type,
          previewUrl,
          status: problem ? 'failed' : 'uploading',
          error: problem ?? undefined,
        };
      });

      setStaged((prev) => [...prev, ...entries]);

      // Upload eagerly at stage time so Send is instant and a rejection surfaces before the
      // agent types their sentence. Cost: a staged-then-removed file leaves an unreferenced
      // blob, which is what the retention sweep's ExpiresAt exists to collect.
      await Promise.all(
        entries.map(async (entry, i) => {
          if (entry.status === 'failed') return;
          try {
            const result = await uploadFile(sessionId, files[i]);

            // The server names the identifier `id`, NOT `fileId`. Reading `fileId` alone
            // silently yielded undefined on every upload in the widget and posted a null
            // attachment id; `fileId` is kept only as a forward-compatible fallback.
            const uploadedId = result?.id ?? result?.fileId;
            if (!uploadedId) {
              setStaged((prev) =>
                prev.map((s) =>
                  s.localId === entry.localId
                    ? {
                        ...s,
                        status: 'failed',
                        error: 'Upload succeeded but the server returned no file id. Please try again.',
                      }
                    : s,
                ),
              );
              return;
            }

            setStaged((prev) =>
              prev.map((s) =>
                s.localId === entry.localId
                  ? {
                      ...s,
                      status: 'ready',
                      uploadedId,
                      fileName: result?.fileName ?? s.fileName,
                      sizeBytes: result?.sizeBytes ?? s.sizeBytes,
                      mimeType: result?.mimeType ?? s.mimeType,
                    }
                  : s,
              ),
            );
          } catch (err: any) {
            const error =
              err?.response?.data?.error ?? err?.message ?? 'Upload failed. Please try again.';
            setStaged((prev) =>
              prev.map((s) => (s.localId === entry.localId ? { ...s, status: 'failed', error } : s)),
            );
          }
        }),
      );
    },
    [mediaConfig, sessionId, staged.length],
  );

  const removeStaged = useCallback(
    (localId: string) => {
      setStaged((prev) => {
        const hit = prev.find((s) => s.localId === localId);
        revoke(hit?.previewUrl);
        return prev.filter((s) => s.localId !== localId);
      });
    },
    [revoke],
  );

  const clearStaged = useCallback(() => {
    setStaged((prev) => {
      prev.forEach((s) => revoke(s.previewUrl));
      return [];
    });
  }, [revoke]);

  const readyIds = staged
    .filter((s) => s.status === 'ready' && s.uploadedId)
    .map((s) => s.uploadedId as string);

  return {
    staged,
    addFiles,
    removeStaged,
    clearStaged,
    readyIds,
    isUploading: staged.some((s) => s.status === 'uploading'),
    canStageMore: staged.length < MAX_STAGED_ATTACHMENTS,
  };
}
